<template>
  <Page title="Imports">
    <template #actions>
      <div class="flex flex-wrap items-center gap-4">
        <span class="text-sm text-gray-500" data-cy="imports-last-updated"
          >Last updated: {{ lastUpdated }}</span
        >
        <Toggle
          label-after="Live updates"
          :checked="liveUpdates"
          data-cy="imports-live-toggle"
          @update:checked="liveUpdates = $event"
        />
        <Button
          label="Refresh"
          type="Secondary"
          outline
          :disabled="refreshing"
          data-cy="imports-refresh"
          @click="refresh"
        />
        <Dropdown
          v-if="newImportItems.length"
          label="New import"
          data-cy="imports-new-import"
          type="Primary"
          :items="newImportItems"
          @update:model-value="goToNewImport"
        />
      </div>
    </template>

    <NavTab
      class="mb-4"
      variant="underline"
      :tabs="navTabs"
      @select="selectTab"
    />

    <div @click="onTableClick">
      <DatatableReportV3
        ref="table"
        title="Imports"
        :source="source"
        :config="config"
        :filters="[]"
        :map-records="requestParams"
        :map-row-class="rowClass"
        hide-header
        no-download
        @draw-complete="onDrawComplete"
      >
        <template #column-status="row">
          <ImportStatusChip :status="row.status" />
        </template>
        <template #column-progress="row">
          <ImportProgressCell :row="row" />
        </template>
        <template #column-actions="row">
          <ImportRowActions
            :row="row"
            @action="(action, file) => handleAction(row, action, file)"
          />
        </template>
      </DatatableReportV3>
    </div>

    <Modal
      v-if="startTarget"
      :model-value="true"
      title="Start import?"
      size="small"
      @update:model-value="startTarget = null"
    >
      <p class="text-gray-700" data-cy="start-import-body">
        {{ startBody }}
      </p>
      <template #footer>
        <div class="flex justify-end gap-2 p-4">
          <Button
            label="Cancel"
            type="Secondary"
            outline
            data-cy="start-import-cancel"
            @click="startTarget = null"
          />
          <Button
            label="Start import"
            data-cy="start-import-confirm"
            @click="confirmStart"
          />
        </div>
      </template>
    </Modal>

    <WipeImportModal
      :open="Boolean(wipeTarget)"
      :name="decodeHtml(wipeTarget?.name)"
      @cancel="wipeTarget = null"
      @confirm="confirmWipe"
    />

    <CancelImportModal
      :open="Boolean(cancelImportTarget)"
      :name="decodeHtml(cancelImportTarget?.name)"
      @cancel="cancelImportTarget = null"
      @confirm="confirmCancelImport"
    />
  </Page>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from "vue";
import { useRouter } from "vue-router";
import { useStore } from "vuex";

import Page from "@/components/Page.vue";
import Button from "@/components/Seranova/Buttons/Button.vue";
import Dropdown from "@/components/Seranova/Dropdown/Dropdown.vue";
import Modal from "@/components/Seranova/Modal/Modal.vue";
import NavTab from "@/components/Seranova/NavTab/NavTab.vue";
import Toggle from "@/components/Seranova/Toggle/Toggle.vue";
import DatatableReportV3 from "@/Reports/DatatableReportV3.vue";
import CancelImportModal from "./CancelImportModal.vue";
import type { NewImportType } from "./flow/types";
import ImportProgressCell from "./ImportProgressCell.vue";
import ImportRowActions from "./ImportRowActions.vue";
import {
  decodeHtml,
  formatClock,
  formatCount,
  formatDateTime,
  type HubFile,
  type HubTab,
  type ImportAction,
  type ImportRow,
  isActive,
  linkFor,
  tabLabel
} from "./importsHubRows";
import ImportStatusChip from "./ImportStatusChip.vue";
import WipeImportModal from "./WipeImportModal.vue";

const props = defineProps<{
  source: string;
  tabs: HubTab[];
  newImports: NewImportType[];
}>();

const POLL_MS = 60_000;
// A failed request never draws, so the in-flight flag is released after far longer than any real
// request takes; off the 60s tick so it can never let two refreshes overlap.
const STUCK_MS = 150_000;
// Import and dry-run jobs start after the request returns; one later look shows the row moving.
const FOLLOW_UP_MS = 5_000;

const store = useStore();
const router = useRouter();
const table = ref<{
  refreshTable: (options?: { resetPaging?: boolean }) => void;
} | null>(null);

const activeType = ref<string | null>(null);
const rows = ref<ImportRow[]>([]);
const lastUpdated = ref("");
const refreshing = ref(false);
const liveUpdates = ref(false);
const startTarget = ref<ImportRow | null>(null);
const wipeTarget = ref<ImportRow | null>(null);
const cancelImportTarget = ref<ImportRow | null>(null);
let pendingRows: ImportRow[] = [];
let queuedRefresh: { resetPaging: boolean } | null = null;
let followUp: ReturnType<typeof setTimeout> | null = null;
let timer: ReturnType<typeof setInterval> | null = null;
let stuckTimer: ReturnType<typeof setTimeout> | null = null;

const tabName = (tab: HubTab) => `${tabLabel(tab.record_type)} (${tab.count})`;

const navTabs = computed(() =>
  props.tabs.map((tab) => ({
    label: tabName(tab),
    active: tab.record_type === activeType.value
  }))
);

const newImportItems = computed(() =>
  props.newImports.map(({ record_type: type, href }) => ({
    label: tabLabel(type),
    href
  }))
);

const startBody = computed(() => {
  const row = startTarget.value;
  if (!row) return "";
  const count = row.rows ?? 0;
  return `${formatCount(count)} ${count === 1 ? "row" : "rows"} from ${decodeHtml(
    row.name
  )} will be imported.`;
});

const NAME_LINK_CLASS = "import-name-link text-blue-600 hover:underline";

// The name arrives HTML-escaped and the default cell rendering inserts it as HTML.
const nameCell = (name: string, type: string, row: ImportRow) =>
  type === "display" && row.links?.review
    ? `<a class="${NAME_LINK_CLASS}" href="${row.links.review.replace(
        /"/g,
        "&quot;"
      )}">${name}</a>`
    : name;

const plain = { orderable: false, searchable: false };
const config = {
  columns: [
    { data: "name", title: "Name", render: nameCell },
    {
      data: "record_type",
      title: "Type",
      ...plain,
      render: (type: string) => tabLabel(type)
    },
    { data: "vendor", title: "Vendor", ...plain },
    {
      data: "rows",
      title: "Rows",
      ...plain,
      render: (count: number | null) =>
        count == null ? "" : formatCount(count)
    },
    { data: "status", title: "Status", ...plain },
    { data: "progress", title: "Progress", ...plain },
    {
      data: "created_at",
      title: "Created",
      searchable: false,
      render: (at: string, type: string) =>
        type === "display" && at ? formatDateTime(at) : at
    },
    { data: "actions", title: "Action", ...plain }
  ],
  order: [[6, "desc"]],
  // The datatable does not expose its rows, so collect them as each draw builds them.
  createdRow: (_node: HTMLElement, data: ImportRow) => pendingRows.push(data)
};

// Runs at the start of every request, so it also starts a fresh row collection.
function requestParams(params: Record<string, unknown>) {
  pendingRows = [];
  if (activeType.value) params.record_type = activeType.value;
  return params;
}

// The datatable adds these with classList.add, so they must be separate tokens.
const rowClass = ({ data }: { data: ImportRow }) =>
  data.status === "wiped"
    ? ["bg-gray-50", "text-gray-400", "[&_a]:text-gray-400"]
    : [];

function refresh(options: { resetPaging: boolean } = { resetPaging: false }) {
  if (!table.value) return;
  if (refreshing.value) {
    // Keep the stronger request: a tab switch's page reset must survive a poll already in flight.
    queuedRefresh = {
      resetPaging: options.resetPaging || !!queuedRefresh?.resetPaging
    };
    return;
  }
  refreshing.value = true;
  stuckTimer = setTimeout(() => {
    refreshing.value = false;
  }, STUCK_MS);
  table.value.refreshTable(options);
}

function refreshAfterAction() {
  refresh();
  if (followUp) clearTimeout(followUp);
  followUp = setTimeout(() => refresh(), FOLLOW_UP_MS);
}

function onDrawComplete() {
  if (stuckTimer) clearTimeout(stuckTimer);
  rows.value = pendingRows;
  refreshing.value = false;
  lastUpdated.value = formatClock(new Date());
  if (queuedRefresh) {
    const options = queuedRefresh;
    queuedRefresh = null;
    refresh(options);
  }
}

function selectTab(tab: { label: string }) {
  const selected = props.tabs.find(
    (candidate) => tabName(candidate) === tab.label
  );
  if (!selected || selected.record_type === activeType.value) return;
  activeType.value = selected.record_type;
  refresh({ resetPaging: true });
}

function pollTick() {
  if (document.visibilityState !== "visible") return;
  if (!rows.value.some(isActive)) return;
  refresh();
}

function stopPolling() {
  if (timer) clearInterval(timer);
  timer = null;
}

watch(liveUpdates, (on) => {
  stopPolling();
  if (on) timer = setInterval(pollTick, POLL_MS);
});

onBeforeUnmount(() => {
  stopPolling();
  if (stuckTimer) clearTimeout(stuckTimer);
  if (followUp) clearTimeout(followUp);
});

const goToNewImport = (item: { href: string }) => {
  router.push(item.href);
};

// The name links are plain HTML inside the datatable; keep a plain click in the SPA and leave
// modified clicks (new tab) to the browser.
function onTableClick(event: MouseEvent) {
  if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey) {
    return;
  }
  const link = (event.target as HTMLElement).closest<HTMLAnchorElement>(
    "a.import-name-link"
  );
  if (!link) return;
  event.preventDefault();
  router.push(link.getAttribute("href") ?? "");
}

async function dispatchThenRefresh(
  action: string,
  url: string,
  successMessage?: string
) {
  try {
    await store.dispatch(action, url);
    if (successMessage) window.notify.success(successMessage);
  } catch {
    // The API client already toasts the failure; the table still needs the new state.
  } finally {
    refreshAfterAction();
  }
}

async function confirmStart() {
  const url = startTarget.value?.links.import;
  startTarget.value = null;
  if (url) await dispatchThenRefresh("performBatchImport", url);
}

async function confirmWipe() {
  const url = wipeTarget.value?.links.wipe;
  wipeTarget.value = null;
  if (url) await dispatchThenRefresh("wipeImport", url, "Wipe started");
}

async function confirmCancelImport() {
  const url = cancelImportTarget.value?.links.cancel_import;
  cancelImportTarget.value = null;
  if (url) await dispatchThenRefresh("cancelBatchRun", url);
}

type ApiError = {
  response?: { data?: { message?: string } };
  data?: { message?: string };
};

const errorMessage = (error: unknown): string | undefined => {
  const { response, data } = (error ?? {}) as ApiError;
  return response?.data?.message ?? data?.message;
};

async function downloadDryRunReport(url: string) {
  try {
    const { ready, message } = await store.dispatch(
      "downloadDryRunReport",
      url
    );
    if (!ready && message) window.notify.success(message);
  } catch (error) {
    // A still-running dry run answers the readiness probe with a non-2xx, which is info, not failure.
    window.notify.success(
      errorMessage(error) ??
        "Your validation report is being prepared — try again in a moment."
    );
  }
}

async function downloadErrors(url: string) {
  try {
    const { ready, message } = await store.dispatch("downloadBatchErrors", url);
    if (!ready && message) window.notify.success(message);
  } catch (error) {
    const message = errorMessage(error);
    if (message) window.notify.error(message);
  }
}

async function handleAction(
  row: ImportRow,
  action: ImportAction,
  file?: HubFile
) {
  if (action === "download_file") {
    if (file) window.open(file.href, "_blank");
    return;
  }
  if (action === "replace_file") {
    // Review holds the upload details the hub row doesn't carry.
    const review = linkFor(row, "review");
    if (review) router.push({ path: review, query: { replace_file: "1" } });
    return;
  }
  const url = linkFor(row, action);
  if (!url) return;
  switch (action) {
    case "review":
    case "finish_mapping":
    case "fix_mapping":
      router.push(url);
      break;
    case "start_import":
      startTarget.value = row;
      break;
    case "run_dry_run":
      await dispatchThenRefresh("performBatchDryRun", url);
      break;
    case "download_dry_run_report":
      await downloadDryRunReport(url);
      break;
    case "download_errors":
      await downloadErrors(url);
      break;
    case "wipe":
      wipeTarget.value = row;
      break;
    case "cancel_dry_run":
      await dispatchThenRefresh("cancelBatchRun", url);
      break;
    case "cancel_import":
      cancelImportTarget.value = row;
      break;
  }
}
</script>
